import * as _ from "lodash";
import moment from "moment";

import { SessionStore } from "../uidispatcherapi/SessionStore";

import ArticleStore from "./ArticleStore";
import * as BundleSelectionUtil from "../utils/BundleSelectionUtil";
import { trackSearchResult } from "../utils/SearchTrackingUtil";
import * as TrackingUtil from "../utils/TrackingUtil";
import { mapToCountedIds, enhanceWithOfferInfo, DEFAULT_ROWS, DEFAULT_FAVOURITES } from "../utils/SearchUtil";
import ArticleSearchClient, { FavouriteSearchQuery, FavouriteSearchResult } from "../clients/ArticleSearchClient";
import TimeUtil from "../utils/TimeUtil";
import FeedbackActions from "../actions/FeedbackActions";
import { Store } from "../utils/Store";

import {
    SearchQuery,
    SearchResultState,
    SearchResult,
    SearchResultEx,
    SearchResultItem,
    SearchResultEntry
} from "../datatypes/SearchResult";
import FeatureUtil from "../utils/FeatureUtil";

function getInitialSearchResult(): SearchResultState {
    return {
        query: "",
        searchPurpose: null,

        valid: false,
        searchRunning: false,
        loadingMore: false,
        gotArticles: false,

        categoryPath: null,
        favouritesVisible: true,
        facets: [],
        ranges: [],
        options: [],
        sorting: undefined,

        result: null,
        favourites: null
    };
}

function getInitialQuery() {
    return { query: null, requestQuery: null, timeStamp: null, data: null };
}

export type SearchStoreQuery = {
    page: number;
    rows: number;
    favouritesPage?: number;
    amountOfFavourites?: number;
    more: boolean;
    customerId?: string;
    loggedIn: boolean;
    country: string;
    locale: string;
    selectedStoreId: string;
    profile?: string | null | undefined;
} & SearchQuery;

let state: SearchResultState;
let lastQuery: {
    query: string | null | undefined;
    requestQuery: FavouriteSearchQuery | null | undefined;
    timeStamp: any | null | undefined;
    data: SearchStoreQuery | null | undefined;
};

class SearchStore extends Store {
    searchPurpose: "search";
    categoryPurpose: "category";
    replacementPurpose: "replacement";
    promotionsPurpose: "promotions";
    offersPurpose: "offers";
    storeListPurpose: "storeListPurpose";

    constructor() {
        super();
        this.searchPurpose = "search";
        this.categoryPurpose = "category";
        this.replacementPurpose = "replacement";
        this.promotionsPurpose = "promotions";
        this.storeListPurpose = "storeListPurpose";
        this.offersPurpose = "offers";
        this.listenTo(ArticleStore, this._handleArticleUpdate);
        state = getInitialSearchResult();
        lastQuery = getInitialQuery();
    }

    // for testing
    _reset() {
        state = getInitialSearchResult();
        lastQuery = getInitialQuery();
    }

    getState(): SearchResultState {
        return state;
    }

    doSearch(data: SearchStoreQuery) {
        const query = data.q;
        state.searchPurpose = data.searchPurpose;
        state.query = query;
        if (query) {
            if (lastQuery.query !== query) {
                state.favouritesVisible = true;
            }

            state.sorting = data.sorting;
            state.loadingMore = data.more || false;
            state.categoryPath = data.categoryPath;
            state.facets = data.facets;
            state.ranges = data.ranges;
            state.options = data.options;
            state.restrictionId = data.restrictionId;
            state.onlySearchable = data.onlySearchable;

            // If we do not do this and the SessionStore triggers fast twice,
            // the second update is lost because in sessiomStoreUpdate lastQuery would
            // still be undefined (as the first request is maybe not fulfilled yet.
            lastQuery.data = data;

            const requestQuery = this._buildRequestQuery(data, query);
            // not clear how to test here for inequality of the request, has to be refactored???
            if (
                !_.isEqual(requestQuery, lastQuery.requestQuery) ||
                TimeUtil.moreThanAmountOfMinutesAgo(lastQuery.timeStamp, 5)
            ) {
                this._performSearchRequest(requestQuery, query, data);
            }
            this.trigger(state);
        } else {
            state = getInitialSearchResult();
            state.query = query;
            this.trigger(state);
        }
    }

    _buildRequestQuery(data: SearchStoreQuery, query: string): FavouriteSearchQuery {
        const page = data.page ? data.page : 1;
        const rows = data.rows ? data.rows : DEFAULT_ROWS;

        const filter = [];
        // TODO(FD) unify the different facet types?
        if (data.categoryPath) {
            filter.push("category:" + data.categoryPath);
        }
        for (const facet of data.facets || []) {
            for (const value of facet.values) {
                filter.push(facet.key + ":" + value);
            }
        }
        for (const rangeFacet of data.ranges || []) {
            filter.push(rangeFacet.key + ":[" + (rangeFacet.range ? rangeFacet.range.join("~") : "") + "]");
        }
        for (const boolFacet of data.options || []) {
            filter.push(boolFacet.key + ":" + (boolFacet.value ? "true" : "false"));
        }

        const requestQuery: FavouriteSearchQuery = {
            storeId: data.selectedStoreId,
            language: data.locale,
            country: data.country,
            query: query,
            rows: rows,
            page: page,
            sorting: data.sorting,
            filter,
            restriction: data.restrictionId,
            onlySearchable: data.onlySearchable,
            facets: true,
            categories: true
        };

        if (FeatureUtil.isSearchDebugEnabled()) {
            requestQuery.debugQuery = true;
        }

        if (data.loggedIn) {
            const amountOfFavourites = data.amountOfFavourites ? data.amountOfFavourites : DEFAULT_FAVOURITES;
            const favouritesPage = data.favouritesPage || 1;
            Object.assign(requestQuery, {
                customerId: data.customerId,
                amountOfFavourites,
                favouritesPage
            });
        }

        return requestQuery;
    }

    _performSearchRequest(requestQuery: FavouriteSearchQuery, query: string, data: SearchStoreQuery) {
        state.searchRunning = true;
        state.gotArticles = false;
        // The loading state should be triggered to reflect that in the listening components.
        // This worked accidentally before because the SearchResult component shows loading state if there are
        // not results yet or search is still running. For two requests with different storeIds
        // the search results of the first search are still shown while second requests still finishes.
        this.trigger(state);
        ArticleSearchClient.doSearch(requestQuery)
            .then(response =>
                this._doFavouriteSearchCompleted(
                    { regularResults: response, favourites: {} as SearchResult },
                    query,
                    requestQuery,
                    data
                )
            )
            .catch(() => this._doSearchFailed());
    }

    toggleFavourites() {
        state.favouritesVisible = !state.favouritesVisible;
        this.trigger(state);
    }

    _saveSearchResult(
        requestQuery: FavouriteSearchQuery,
        query: string,
        regularResult: SearchResultEx,
        reusedRegularSearch: boolean,
        data: SearchStoreQuery,
        favourites: SearchResultEx | null | undefined,
        reusedFavouriteSearch: boolean = false
    ) {
        lastQuery = { requestQuery: requestQuery, timeStamp: moment(), query: query, data: data };

        state.searchRunning = false;
        state.loadingMore = false;
        state.valid = true;
        state.gotArticles = false;

        state.result = regularResult;
        state.favourites = favourites;

        if (!reusedRegularSearch && !reusedFavouriteSearch) {
            const { amount: amountOfFavourites } = favourites || { amount: 0 };
            const { amount: amountOfRegulars } = regularResult;
            trackSearchResult(query, amountOfRegulars, amountOfFavourites);
        }
        this._queryArticles();
    }

    _doFavouriteSearchCompleted(
        response: FavouriteSearchResult,
        query: string,
        requestQuery: FavouriteSearchQuery,
        data: SearchStoreQuery
    ) {
        const { result: regularResult, reusedSearch: reusedRegularSearch } = this.acceptSearchResult(
            response.regularResults,
            state.result,
            state.loadingMore
        );

        const { result: favourites, reusedSearch: reusedFavouriteSearch } = this.acceptSearchResult(
            response.favourites,
            state.favourites,
            state.loadingMore
        );

        this._saveSearchResult(
            requestQuery,
            query,
            regularResult,
            reusedRegularSearch,
            data,
            favourites,
            reusedFavouriteSearch
        );
    }

    acceptSearchResult(response: SearchResult, currentState: SearchResultEx | null | undefined, loadMore: boolean) {
        let additionalVariantIds = [] as string[];
        if (!_.isNil(response.additionalVariantIds)) {
            additionalVariantIds = response.additionalVariantIds;
        }
        let result: SearchResultEx;
        let reusedSearch;
        if (!currentState || !currentState.resultIds || !loadMore) {
            result = Object.assign({}, response, { result: [], idSearchPosition: {} });
            result.additionalVariantIds = additionalVariantIds;
            reusedSearch = false;
        } else {
            // This is just used and necessary for pagination.
            result = currentState;
            result.page = response.page;
            result.rows = response.rows;
            result.totalPages = response.totalPages;

            // union is actually stable (documentation calls this results being in order)!
            result.resultIds = _.union(currentState.resultIds, response.resultIds);
            result.additionalVariantIds = _.union(currentState.additionalVariantIds, additionalVariantIds);
            // will override old values with new, but retain previous results.
            result.results = Object.assign(currentState.results, response.results);
            reusedSearch = true;
        }
        result.idSearchPosition = mapToCountedIds(result.resultIds);
        return { result, reusedSearch };
    }

    _queryArticles() {
        let queryFavouriteIds = [] as { idToFetch: string; variantId: string }[];
        let queryRegularIds = [] as { idToFetch: string; variantId: string }[];
        const result = state.result;
        if (result && result.resultIds && result.resultIds.length > 0) {
            queryRegularIds = enhanceWithOfferInfo(
                result.resultIds.concat(result.additionalVariantIds),
                result.results
            );
        } else if (result) {
            result.result = [];
        }
        const favourites = state.favourites;
        if (favourites && favourites.resultIds && favourites.resultIds.length > 0) {
            queryFavouriteIds = enhanceWithOfferInfo(
                favourites.resultIds.concat(favourites.additionalVariantIds),
                favourites.results
            );
        } else if (favourites) {
            favourites.result = [];
        }
        const allIds = _.union(queryRegularIds, queryFavouriteIds);
        const variantLookupMap = {} as { [variantId: string]: string };
        allIds.forEach(id => {
            variantLookupMap[id.variantId] = id.idToFetch;
        });
        ArticleStore.fetchArticlesWithPreselection(
            allIds.map(id => id.idToFetch),
            variantLookupMap
        );
    }

    _handleArticleUpdate() {
        const result = state.result;
        const favourites = state.favourites;
        if ((result && result.results) || (favourites && favourites.results)) {
            if (result && result.results) {
                const mappedArticles = this._mapSearchArticles(result, "SearchResult");
                result.result = _.compact(mappedArticles);
            }
            if (favourites && favourites.results) {
                const mappedArticles = this._mapSearchArticles(favourites, "FavouriteResult");
                favourites.result = _.compact(mappedArticles);
            }
            state.gotArticles = true;
            this.trigger(state);
        }
    }

    _mapSearchArticles(result: SearchResultEx, type: "FavouriteResult" | "SearchResult"): Array<SearchResultItem> {
        return _.chain(result.resultIds)
            .map(id => {
                return _.get(result.results, id);
            })
            .compact()
            .map((r: SearchResultEntry) => {
                const idWithOffer = enhanceWithOfferInfo([r.id], result.results)[0].idToFetch;
                const articleStoreData = ArticleStore.getOneArticleData(idWithOffer);
                const customerStore = SessionStore.getCustomerStore();

                const successor = this._getSuccessorInDeliveryAssortment(r.successorVariantId, customerStore);

                if (!articleStoreData) {
                    return {
                        state: "loading",
                        type: type,
                        id: idWithOffer
                    };
                }
                if ("state" in articleStoreData) {
                    return {
                        state: articleStoreData.state,
                        type: type,
                        id: idWithOffer
                    } as any;
                }

                const bundle = BundleSelectionUtil.getBundleToShow(articleStoreData.bundles, customerStore);

                const debugInfo =
                    r.score && r.indexingTime
                        ? {
                              score: r.score,
                              indexingTime: r.indexingTime,
                              explain: r.explain
                          }
                        : undefined;

                const item: SearchResultItem = {
                    state: "loaded",
                    type: type,
                    id: idWithOffer,
                    availability: _.get(bundle, ["customerAvailability"]),
                    variant: articleStoreData,
                    successor: successor,
                    onList: r.onList,
                    recentlyOrdered: r.recentlyOrdered,
                    position: result.idSearchPosition[r.id],
                    debugInfo
                };
                return item;
            })
            .value();
    }

    _getSuccessorInDeliveryAssortment(id: string, storeId: string | null | undefined) {
        const article = ArticleStore.getOneArticleData(id) || { bundles: {} };
        if (!article || "state" in article) {
            return undefined;
        }
        const someAreDeliveryBundles = _.some(article.bundles, bundle => {
            const storeSettings = storeId ? bundle.stores[storeId] || {} : {};
            return storeSettings.delivery;
        });
        return someAreDeliveryBundles ? article : undefined;
    }

    _doSearchFailed() {
        TrackingUtil.trackUIError("SearchStore", "Search failed.", state.query);
        FeedbackActions.NewFeedback("danger", "EX_SEARCH_ERROR", "", "", 5000, undefined);
        state.searchRunning = false;
        // Obviously, failed search is not valid.
        state.valid = false;
        this.trigger(state);
    }
}

export default new SearchStore();
